/* eslint-disable no-use-before-define */
/* eslint-disable no-shadow */
/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/consistent-type-assertions */

import { fetchPage } from '@drapejs/core';
import { Invoker, defineReceiver } from '@drapejs/invoker';
import {
  search,
  sales,
  batch,
  products,
  configure,
} from '@distancify/drapejs-litium';
import {
  fetchPageReceiver,
  fetchDataSource,
  fetchDataSourceReceiver,
  setPathResolver,
  fetchStories,
  fetchStoriesReceiver,
  serverRedirects,
} from '@distancify/drapejs-storyblok';

import { gql } from 'graphql-request';

import * as user from '@/connectors/user';
import * as contactForm from '@/connectors/contact-form';
import * as myPages from '@/connectors/my-pages';
import * as productListing from '@/connectors/product-listing';
import * as categoryData from '@/connectors/category-data';
import * as litiumReviews from '@/connectors/reviews';
import * as rentals from '@/connectors/rentals';
import * as customerPrices from '@/connectors/customer-prices';
import * as addToCart from '@/connectors/add-to-cart';
import * as bellaliteSales from '@/connectors/sales';
import * as bellaliteSearch from '@/connectors/search';

import pdp from '@/connectors/pdp';

import * as tycka from '@/connectors/tycka';
import * as productBlock from '@/connectors/product-block';
import * as variantBundles from '@/connectors/variant-bundles';
import * as checkout from '@/connectors/checkout';
import * as storyblok from '@/connectors/storyblok';
import * as brands from '@/connectors/brand-index';

import { EnvironmentOptions } from './EnvironmentOptions';

export default function (env: EnvironmentOptions, invoker: Invoker) {
  setPathResolver((command, _baseResolver) => {
    if (command.path.startsWith('/nyheter/')) {
      return command.path;
    }

    if ((<any>command).error === 404) {
      command.path = '/not-found';
    }

    let newPath = '';
    const pathWithoutSiteId = command.path.startsWith(`/${env.siteId}`)
      ? command.path.substring(env.siteId.length + 1)
      : command.path;
    const pathParts = pathWithoutSiteId.split('/').filter((s) => s);

    if (
      env.maxStoryblokFolderDepth &&
      pathParts.length > +env.maxStoryblokFolderDepth + 1
    ) {
      const maintainedPath = [...pathParts]
        .splice(0, +env.maxStoryblokFolderDepth)
        .join('/');
      const reducedPathToSaveFolders = [...pathParts]
        .splice(+env.maxStoryblokFolderDepth, pathParts.length - 1)
        .join('__');

      newPath = `/${env.siteId}/${maintainedPath}/${reducedPathToSaveFolders}`;
    } else {
      newPath = `/${env.siteId}/${pathParts.join('/')}`;
    }

    return newPath;
  });

  invoker
    .configureCommand(fetchPage)
    .addReceiver(
      defineReceiver(fetchPage, async function (command, data) {
        if (env?.siteId?.toLowerCase() !== 'company') {
          await user.scheduleRefreshUserReceiver.call(this, command, data);
          batch.executeBathQueryReceiver.call(this, command, data);
        }

        return data;
      })
    )
    .addReceiver(fetchPageReceiver)
    .addReceiver(serverRedirects)
    .addReceiver(
      defineReceiver(fetchPage, async function (command, data) {
        if (data.http_status === 404) {
          (<any>command).error = 404;
          data = await fetchPageReceiver.call(this, command, data);
          data.http_status = 404;
          await this.cache.setItem(command.cacheKey, data);
        }

        if (command.cacheKey.includes('settings')) {
          this.break();
        }

        (<any>command).url = `${(<any>command).protocol}//${
          (<any>command).host
        }${(<any>command).path}`;

        return data;
      })
    )
    .addReceiver(storyblok.loadBlogReceiver)
    .addReceiver(
      defineReceiver(fetchPage, async function (command, data) {
        if (env?.siteId?.toLowerCase() === 'company') {
          this.break();
        }

        return data;
      })
    )
    .addReceiver(
      defineReceiver(fetchPage, async function (command, data) {
        await this.invoke(batch.scheduleQuery, {
          query: gql`
              channel {
                rootPath
                systemId
                isBlack
                isWhite
                locale
                countryRegexes
                counties {
                  value
                  text
                }
                metaProduct {
                  fieldsJson
                }
                rootProductsCategory
                website {
                  websiteFields
                  systemId
                }
              }
            `,
          cacheKey: '__channel',
        });

        await this.invoke(batch.scheduleQuery, {
          query: gql`categoryTree`,
          cacheKey: '__categoryTree',
        });

        return data;
      })
    )
    .addReceiver(categoryData.schedule)
    .addReceiver(checkout.scheduleRefreshCartReceiver)
    .addReceiver(productListing.schedule)
    .addReceiver(brands.schedule)
    .addReceiver(pdp)
    .addReceiver(litiumReviews.scheduleGetReviewer)
    .addReceiver(myPages.receivers.fetchMyOrder)
    .addReceiver(batch.executeBathQueryReceiver)
    .addReceiver(
      defineReceiver(fetchPage, async function (command, data) {
        await variantBundles.scheduleAll.call(this, command, data);
        await productBlock.scheduleAll.call(this, command, data);
        batch.executeBathQueryReceiver.call(this, command, data);
        return data;
      })
    );

  invoker.configureCommand(fetchStories).addReceiver(fetchStoriesReceiver);

  invoker
    .configureCommand(fetchDataSource)
    .addReceiver(fetchDataSourceReceiver);

  invoker
    .configureCommand(batch.executeBatchQuery)
    .addReceiver(batch.executeBathQueryReceiver);

  invoker
    .configureCommand(batch.scheduleQuery)
    .addReceiver(batch.scheduleQueryReceiver);

  invoker
    .configureCommand(sales.scheduleRefreshCart)
    .addReceiver(sales.scheduleRefreshCartReceiver);

  invoker
    .configureCommand(sales.refreshCart)
    .addReceiver(sales.refreshCartReceiver);

  invoker
    .configureCommand(bellaliteSales.commands.addToCart)
    .addReceiver(bellaliteSales.receivers.addToCart);

  invoker
    .configureCommand(bellaliteSales.commands.setRowQuantity)
    .addReceiver(bellaliteSales.receivers.setRowQuantity);

  invoker
    .configureCommand(bellaliteSales.commands.setLogTradeDeliveryOption)
    .addReceiver(bellaliteSales.receivers.setLogTradeDeliveryOption);

  invoker
    .configureCommand(sales.placeDirectPayOrder)
    .addReceiver(sales.placeDirectPayOrderReceiver);

  invoker
    .configureCommand(sales.resetCartToAvailableStock)
    .addReceiver(sales.resetCartToAvailableStockReceiver);

  invoker.configureCommand(sales.getOrder).addReceiver(sales.getOrderReceiver);

  invoker
    .configureCommand(sales.setDiscountCode)
    .addReceiver(sales.setDiscountCodeReceiver);

  invoker
    .configureCommand(sales.subscribeToNewsletter)
    .addReceiver(sales.subscribeToNewsletterReceiver);

  invoker
    .configureCommand(bellaliteSearch.commands.performQuickSearch)
    .addReceiver(bellaliteSearch.receivers.performQuickSearch);

  invoker
    .configureCommand(productListing.commands.loadMoreProducts)
    .addReceiver(productListing.receivers.loadMoreProducts);

  invoker
    .configureCommand(search.commands.fetchCategoryProducts)
    .addReceiver(search.receivers.fetchCategoryProducts);

  invoker
    .configureCommand(search.commands.fetchMoreCategoryProducts)
    .addReceiver(search.receivers.fetchMoreCategoryProducts);

  invoker
    .configureCommand(products.fetchBaseProduct)
    .addReceiver(products.fetchBaseProductReceiver);

  invoker
    .configureCommand(addToCart.commands.fetchProduct)
    .addReceiver(addToCart.receivers.fetchProduct);

  invoker
    .configureCommand(addToCart.commands.fetchVariant)
    .addReceiver(addToCart.receivers.fetchVariant);

  invoker
    .configureCommand(user.commands.changeOrganization)
    .addReceiver(user.receivers.changeOrganization);

  invoker
    .configureCommand(user.commands.validateEmail)
    .addReceiver(user.receivers.validateEmail);

  invoker
    .configureCommand(user.commands.login)
    .addReceiver(user.receivers.login);

  invoker
    .configureCommand(user.commands.logout)
    .addReceiver(user.receivers.logout);

  invoker
    .configureCommand(user.commands.forgotPassword)
    .addReceiver(user.receivers.forgotPassword);

  invoker
    .configureCommand(user.commands.validateResetPasswordCode)
    .addReceiver(user.receivers.validateResetPasswordCode);

  invoker
    .configureCommand(user.commands.resetPassword)
    .addReceiver(user.receivers.resetPassword);

  invoker
    .configureCommand(user.commands.fetchMyOrderDetails)
    .addReceiver(user.receivers.fetchMyOrderDetails);

  invoker
    .configureCommand(user.commands.fetchMyInvoiceDetails)
    .addReceiver(user.receivers.fetchMyInvoiceDetails);

  invoker
    .configureCommand(user.commands.updatePerson)
    .addReceiver(user.receivers.updatePerson);

  invoker
    .configureCommand(user.commands.sendEmailVerification)
    .addReceiver(user.receivers.sendEmailVerification);

  invoker
    .configureCommand(user.commands.changePassword)
    .addReceiver(user.receivers.changePassword);

  invoker
    .configureCommand(user.commands.changeChannel)
    .addReceiver(user.receivers.changeChannel);

  invoker
    .configureCommand(user.commands.updateBillingAddress)
    .addReceiver(user.receivers.updateBillingAddress);

  invoker
    .configureCommand(user.commands.addOrUpdateShippingAddress)
    .addReceiver(user.receivers.addOrUpdateShippingAddress);

  invoker
    .configureCommand(user.commands.deleteShippingAddress)
    .addReceiver(user.receivers.deleteShippingAddress);

  invoker
    .configureCommand(user.commands.addOrUpdateContact)
    .addReceiver(user.receivers.addOrUpdateContact);

  invoker
    .configureCommand(user.commands.subscribeToAudience)
    .addReceiver(user.receivers.subscribeToAudience);

  invoker
    .configureCommand(user.commands.unsubscribeFromAudience)
    .addReceiver(user.receivers.unsubscribeFromAudience);

  invoker
    .configureCommand(user.commands.validateOrganization)
    .addReceiver(user.receivers.validateOrganization);

  invoker
    .configureCommand(user.commands.registerOrganization)
    .addReceiver(user.receivers.registerOrganization);

  invoker
    .configureCommand(user.commands.refreshUser)
    .addReceiver(user.receivers.refreshUser);

  invoker
    .configureCommand(user.commands.setShippingAddress)
    .addReceiver(user.receivers.setShippingAddress);

  invoker
    .configureCommand(user.commands.fetchInstallers)
    .addReceiver(user.receivers.fetchInstallers);

  invoker
    .configureCommand(user.commands.addOrUpdateInstaller)
    .addReceiver(user.receivers.addOrUpdateInstaller);

  invoker
    .configureCommand(user.commands.removeInstaller)
    .addReceiver(user.receivers.removeInstaller);

  invoker
    .configureCommand(myPages.commands.fetchMyOrders)
    .addReceiver(myPages.receivers.fetchMyOrders);

  invoker
    .configureCommand(bellaliteSales.commands.changeDeliveryPreference)
    .addReceiver(bellaliteSales.receivers.changeDeliveryPreference);

  invoker
    .configureCommand(bellaliteSales.commands.placeWholesaleOrder)
    .addReceiver(bellaliteSales.receivers.placeWholesaleOrder);

  invoker
    .configureCommand(contactForm.commands.submitContactForm)
    .addReceiver(contactForm.receivers.submitContactForm);

  rentals.configureRentalCommands(invoker);
  customerPrices.configureCustomerPricesCommands(invoker);
  litiumReviews.configureReviewsCommands(invoker);
  tycka.configureTyckaCommands(invoker);

  configure({
    fetchPage: 'title',
    cart: getCartFields(),
    cartRow: getCartRowFields(),
    receipt: getReceiptFields(),
    productSearch: '',
    baseProduct: getBaseProductFields(),
  });

  tycka.init(invoker.cache, `https://api.tycka.io/${env.tyckaAccount}/graphql`);
}

function getReceiptFields() {
  return `
  shippingDate
  rows {
    delivery {
      deliveryId
      promisedDeliveryDate
      shippingOption
    }
  }
  `;
}

function getCartFields() {
  return `
    comments
    shippingDateTime
    expectedShippingDateTime
    estimatedShippingDateTime
    projectReference
    cartWeight
    cartVolume
    inventoryCountry
    deliveryOptionId
    deliveryPreferences {
      deliveryPreference
      isActive
    }
    addresses {
      no
      code
      systemId
      address
      address2
      postCode
      country
      city
      organizationName
      name
      isSelected
      isSavedOnTheOrganization
    }
    contactPerson {
      firstName
      lastName
      email
      phone
    }
    countries {
      value: code
      text: name
    }
    countryId
    logTradeDeliveryOptions {
      optionId
      description
      productName
      logotypeUrl
    }
  `;
}

function getCartRowFields() {
  return `
    googleAnalyticsName
    baseProductId
    unitOfMeasurement
    productId
    productVolume
    productWeight
    baseProductName
    variantLabel
    quantityMultiplier
    originalPrice
    stock {
      totalStockAvailable
      currentStock {
        amount
        date
      }
      incomingStock {
        date
        amount
      }
      expectedShippingDateTime
      deliveryDateFromSupplier
    }
    inactive
  `;
}

function getBaseProductFields() {
  return `
    id,
    fieldsJson
    images {
      id
      filename
    }
    purchaseQuantityMultiplier
    variants {
      variantLabel
      baseProductUrl,
      articleNumber,
      systemId,
      purchaseQuantityFactor
      fields,
      unitOfMeasurement
      images {
        id
        filename
      }
      prices {
        originalPrice
        campaignPrice
      }
      priceFlag
      inStock
      stock {
        totalStockAvailable
        currentStock {
          amount
          date
        }
        incomingStock {
          date
          amount
        }
        expectedShippingDateTime
        deliveryDateFromSupplier
      }
      productRentalState {
        isRentedByCurrentOrganization
        isRentedByAnyOrganization
      }
      technicalSpecifications {
        id
        name
        value
      }
    }
    technicalSpecifications {
      id
      name
      value
    }
  `;
}
